<template>
    <FormSection
        title="Admission Information"
        :full-height="false"
        validation-renderer="bootstrap-form-renderer"
        :application-id="application"
    >
        <template #content>
            <div class="row mt-2 px-4">
                <div class="col-12 offset-md-1 col-md-10 row g-0">
                    <div class="form-group col-12 col-md-4 pe-lg-2">
                        <label for="admission-type">
                            Type of Admission
                            <span class="text-danger">*</span>
                        </label>
                        <select
                            name="admission-type"
                            class="form-select"
                            v-model="patientDetails.admissionType"
                            @blur="v$.patientDetails.admissionType.$touch"
                        >
                            <option :value="null">Select Type of Admission</option>
                            <option v-for="[key, value] of enumToMap(AdmissionTypes)" :value="value.value">
                                {{ value.name }}
                            </option>
                        </select>
                        <span v-if="v$.patientDetails.admissionType.$error" class="field-invalid"
                            >Admission Type is required</span
                        >
                    </div>
                    <div class="form-group col-12 col-md-4 pe-lg-2">
                        <label for="admission-type">
                            Admission Source
                            <span class="text-danger">*</span>
                        </label>
                        <select
                            name="admission-type"
                            class="form-select"
                            v-model="patientDetails.admissionSource"
                            @blur="v$.patientDetails.admissionSource.$touch"
                        >
                            <option :value="null">Select Admission Source</option>
                            <option v-for="[key, value] of enumToMap(AdmissionSources)" :value="value.value">
                                {{ value.name }}
                            </option>
                        </select>
                        <span v-if="v$.patientDetails.admissionSource.$error" class="field-invalid"
                            >Admission Source is required</span
                        >
                    </div>
                    <div class="col-12 d-flex flex-wrap mt-2">
                        <div class="form-check custom-radio form-check-inline">
                            <input
                                type="radio"
                                id="patientDetails-new-admission"
                                name="admission-source"
                                class="form-check-input au-target"
                                :checked="!patientDetails.isTransfer"
                                @change="handleAdmissionSourceTypeSelection()"
                                :disabled="false"
                            />
                            <label class="form-check-label" for="patientDetails-new-admission">
                                <span class="me-1">New Admission</span>
                            </label>
                        </div>
                    </div>
                    <div class="col-12 mt-3 row g-0">
                        <template v-if="!patientDetails.isTransfer">
                            <div class="form-group col-12 col-lg-4 pe-lg-2">
                                <label for="benefit-period-number">
                                    Benefit Period Number <span class="text-danger ms-1">*</span>
                                </label>
                                <input
                                    class="form-control"
                                    type="text"
                                    name="benefit-period-number"
                                    placeholder="Enter Benefit Period Number"
                                    v-model="patientDetails.benefitPeriodNumber"
                                    v-number-only
                                    :disabled="enableAdmissionBenefitPeriod"
                                    @blur="v$.patientDetails.benefitPeriodNumber.$touch"
                                />
                                <span v-if="v$.patientDetails.benefitPeriodNumber.$error" class="field-invalid"
                                    >Benefit Period is required</span
                                >
                            </div>
                            <div class="form-group col-12 col-lg-4 pe-lg-2">
                                <label for="benefit-period-number"> Routine Home Care Low Rate Start Date </label>
                                <p>Pending Admission Date</p>
                            </div>
                            <div
                                class="form-group col-12 col-lg-12 mb-0"
                                v-if="Number(patientDetails.benefitPeriodNumber) >= 2"
                            >
                                <label for="previous-hospice-admission">
                                    Previous Hospice Admission Within Last 60 days

                                    <i
                                        class="fas fa-info-circle text-primary font-size-xs"
                                        v-tooltip="{
                                            title: 'Select this option if the patient was discharged from hospice and then readmitted within 60 days of their previous discharge using the Medicare hospice benefit.'
                                        }"
                                    ></i>
                                </label>
                                <div class="custom-control custom-radio custom-control-inline">
                                    <input
                                        type="radio"
                                        id="hospiceAdmissionNo"
                                        name="hospiceAdmission"
                                        class="custom-control-input me-2"
                                        v-model="patientDetails.previousHospiceAdmission"
                                        :value="false"
                                        @change="handlePreviousAdmissionChange(false)"
                                    />
                                    <label class="custom-control-label" for="hospiceAdmissionNo"> No </label>
                                </div>
                                <div class="custom-control custom-radio custom-control-inline mt-1">
                                    <input
                                        type="radio"
                                        id="hospiceAdmissionYes"
                                        name="hospiceAdmission"
                                        class="custom-control-input me-2"
                                        v-model="patientDetails.previousHospiceAdmission"
                                        :value="true"
                                        @change="handlePreviousAdmissionChange(true)"
                                    />
                                    <label class="custom-control-label" for="hospiceAdmissionYes"> Yes </label>
                                </div>

                                <div
                                    class="form-group col-12 col-lg-4 pe-lg-4"
                                    v-if="
                                        Number(patientDetails.benefitPeriodNumber) >= 2 &&
                                        patientDetails.previousHospiceAdmission === true
                                    "
                                >
                                    <label for="benefit-period-number">
                                        Previous Enrollment Days
                                        <i
                                            class="fas fa-info-circle text-primary font-size-xs"
                                            v-tooltip="{
                                                title: 'Enter the number of days the patient received hospice services under the Medicare hospice benefit during their previous admission. This value is used to determine the transition from the routine home care high rate to the routine home care low rate.'
                                            }"
                                        ></i>
                                    </label>
                                    <input
                                        class="form-control"
                                        type="text"
                                        name="previous-enrollment-days"
                                        placeholder="Previous Enrollment Days"
                                        v-model="patientDetails.previousEnrollmentDays"
                                    />
                                </div>
                            </div>
                        </template>
                    </div>
                    <div class="form-check custom-radio form-check-inline pb-3">
                        <input
                            type="radio"
                            id="patientDetails-hospice-to-hospice"
                            name="admission-source"
                            class="form-check-input au-target"
                            :checked="patientDetails.isTransfer"
                            @change="handleAdmissionSourceTypeSelection()"
                            :disabled="false"
                        />
                        <label class="transfer-text" for="patientDetails-hospice-to-hospice">
                            <span class="me-1">Hospice to Hospice Transfer</span>
                        </label>
                    </div>
                    <template v-if="patientDetails.isTransfer">
                        <HospiceTransferForm
                            :benefitPeriodStartDate="benefitPeriodStartDate"
                            :handleDateChange="handleBenfitPeriodStartDate"
                            :benefitPeriodEndDate="benefitPeriodEndDate"
                            :homePhoneNumberOptions="homePhoneNumberOptions"
                            :isReadOnly="isPendingStatus"
                        />
                    </template>
                    <a
                        class="fw-bold"
                        @click="$router.push('/patients/benefit-period-manager/${patient.id}')"
                        v-if="showBenefitPeriodManager && hasViewBenefitPeriodPermission"
                    >
                        Benefit Period Manager
                    </a>
                </div>
            </div>
        </template>
    </FormSection>
</template>

<script lang="ts" src="./AdmissionInformation.ts"></script>
<style lang="scss" src="./AdmissionInformation.scss"></style>
